import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  Upload, X, FileText, Image as ImageIcon, ArrowLeft, Sparkles, ChevronDown, ChevronUp, Loader2,
  ClipboardList, Heart, Microscope, TrendingUp, Palette, Lightbulb, Link as LinkIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import { UserContext } from '../App';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 5 Dimensions (removed inspired_work as Projects is now separate)
const DIMENSIONS = [
  { value: '', label: 'No Dimension (General Task)', icon: null },
  { value: 'cness_tasks', label: 'Tasks', icon: ClipboardList },
  { value: 'self_care', label: 'Self Care', icon: Heart },
  { value: 'rnd', label: 'R&D', icon: Microscope },
  { value: 'personal_growth', label: 'Personal Growth', icon: TrendingUp },
  { value: 'creative', label: 'Creative', icon: Palette },
];

const TaskCreate = ({ user, isEditMode = false }) => {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const location = useLocation();
  
  // Get query params
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('project_id');
  const dimensionFromUrl = queryParams.get('dimension');
  
  // Auto-detect dimension from URL path or query param
  const getDimensionFromPath = () => {
    if (dimensionFromUrl) return [dimensionFromUrl];
    const path = location.pathname;
    if (path.includes('/self-care')) return ['self_care'];
    if (path.includes('/rnd')) return ['rnd'];
    if (path.includes('/personal-growth')) return ['personal_growth'];
    if (path.includes('/creative')) return ['creative'];
    if (path.includes('/tasks') || path.includes('/cness-tasks')) return ['cness_tasks'];
    return []; // Default: no dimension
  };

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [subprojects, setSubprojects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_id: projectIdFromUrl || '',
    subproject_id: '',
    dimension: getDimensionFromPath(), // Auto-set based on current page
    assigned_to: [],
    work_along_with: [],
    priority: 5,
    deadline: '',
    research_notes: '',
    document_links: [],
    is_common_task: false  // New: explicit common task flag
  });
  const [attachments, setAttachments] = useState([]);
  const [newDocLink, setNewDocLink] = useState({ title: '', url: '' });
  
  // AI Assistant State
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      await fetchUsers();
      await fetchProjects();
      if (isEditMode && taskId) {
        await fetchTask();
      }
    };
    loadData();
  }, [taskId, isEditMode]);

  // Fetch sub-projects when project changes
  useEffect(() => {
    if (formData.project_id) {
      fetchSubProjects(formData.project_id);
    } else {
      setSubprojects([]);
      setFormData(prev => ({ ...prev, subproject_id: '' }));
    }
  }, [formData.project_id]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.filter(p => p.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  const fetchSubProjects = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/projects/${projectId}/subprojects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubprojects(response.data.filter(sp => sp.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch sub-projects');
      setSubprojects([]);
    }
  };

  const fetchTask = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const task = response.data;
      setFormData({
        title: task.title,
        description: task.description,
        project_id: task.project_id || '',
        subproject_id: task.subproject_id || '',
        dimension: Array.isArray(task.dimension) ? task.dimension : (task.dimension ? [task.dimension] : []),
        assigned_to: task.assigned_to || [],
        work_along_with: task.work_along_with || [],
        priority: task.priority || 5,
        deadline: task.deadline ? task.deadline.split('T')[0] : '',
        research_notes: task.research_notes || '',
        document_links: task.document_links || []
      });
      setAttachments(task.attachments || []);
    } catch (error) {
      toast.error('Failed to fetch task details');
      navigate('/dashboard/tasks');
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Store all users for "Work Along With" section
      setUsers(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  // Get filtered users for "Assigned To" based on manager/lead hierarchy
  const getAssignableUsers = () => {
    if (!user) return [];
    
    if (user.role === 'manager' || user.role === 'team_lead') {
      // Managers and Team Leads can only assign to their direct reports
      return users.filter(u => 
        (u.role === 'team_member' || u.role === 'team_lead') && 
        u.manager_ids && 
        u.manager_ids.includes(user.id)
      );
    }
    
    // CEO and Superadmin can assign to anyone
    return users;
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 10MB`);
        continue;
      }

      // Expanded file types
      const allowedTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
        'application/pdf',
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain', 'text/csv',
        'application/zip', 'application/x-rar-compressed'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not supported. Allowed: Images, PDF, Word, Excel, PPT, TXT, CSV, ZIP`);
        continue;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        setAttachments(prev => [...prev, {
          filename: file.name,
          content_type: file.type,
          data: event.target.result.split(',')[1], // Remove data:image/png;base64, prefix
          size: file.size
        }]);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      // Convert empty string to null for common tasks and deadline
      const taskData = {
        ...formData,
        project_id: formData.project_id === '' ? null : formData.project_id,
        subproject_id: formData.subproject_id === '' ? null : formData.subproject_id,
        assigned_to: formData.assigned_to || [],
        work_along_with: formData.work_along_with || [],
        deadline: formData.deadline ? formData.deadline : null,  // Store as YYYY-MM-DD string, no timezone conversion
        research_notes: formData.research_notes || '',
        document_links: formData.document_links || [],
        attachments
      };
      
      if (isEditMode && taskId) {
        // Update existing task
        await axios.put(
          `${API_URL}/tasks/${taskId}`,
          taskData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Task updated successfully!');
      } else {
        // Create new task
        await axios.post(
          `${API_URL}/tasks`,
          taskData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Task created successfully!');
      }
      
      navigate('/dashboard/tasks');
    } catch (error) {
      console.error('Task creation error:', error);
      const errorMessage = error.response?.data?.detail || error.message || `Failed to ${isEditMode ? 'update' : 'create'} task`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addDocumentLink = () => {
    if (newDocLink.url.trim()) {
      setFormData({
        ...formData,
        document_links: [...formData.document_links, { 
          title: newDocLink.title || newDocLink.url, 
          url: newDocLink.url 
        }]
      });
      setNewDocLink({ title: '', url: '' });
    }
  };

  const removeDocumentLink = (index) => {
    setFormData({
      ...formData,
      document_links: formData.document_links.filter((_, i) => i !== index)
    });
  };

  const getFileIcon = (contentType) => {
    if (contentType.startsWith('image/')) {
      return <ImageIcon size={24} className="text-purple-500" />;
    }
    return <FileText size={24} className="text-blue-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            onClick={() => navigate('/dashboard/tasks')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#7077FE',
              display: 'flex',
              alignItems: 'center',
              fontSize: '16px'
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <h2>{isEditMode ? 'Edit Task' : 'Create New Task'}</h2>
        </div>
      </div>

      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* Title */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Task Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Enter task title"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Description *
              </label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                placeholder="Enter task description with formatting..."
                minHeight="150px"
              />
            </div>

            {/* Project Selection */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Project
              </label>
              <select
                value={formData.project_id}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value, subproject_id: '' })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
                data-testid="project-select"
              >
                <option value="">No Project (Standalone Task)</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                Link this task to a project for better organization
              </p>
            </div>

            {/* Sub-Project Selection (Only shown when project is selected) */}
            {formData.project_id && subprojects.length > 0 && (
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Sub-Project
                </label>
                <select
                  value={formData.subproject_id}
                  onChange={(e) => setFormData({ ...formData, subproject_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)'
                  }}
                  data-testid="subproject-select"
                >
                  <option value="">No Sub-Project</option>
                  {subprojects.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.title}
                    </option>
                  ))}
                </select>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                  Optionally link this task to a sub-project
                </p>
              </div>
            )}

            {/* Dimension is auto-detected based on current page - hidden from UI */}
            {formData.dimension.length > 0 && (
              <div style={{ display: 'none' }}>
                <input type="hidden" name="dimension" value={JSON.stringify(formData.dimension)} />
              </div>
            )}

            {/* Common Task Toggle */}
            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #F59E0B15, #F59E0B05)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={formData.is_common_task}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({ ...formData, is_common_task: true, assigned_to: [] });
                    } else {
                      setFormData({ ...formData, is_common_task: false });
                    }
                  }}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  data-testid="common-task-checkbox"
                />
                <div>
                  <span style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text-primary)' }}>
                    Make this a Common Task
                  </span>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Common tasks are available for anyone to pick up. No specific assignment required.
                  </p>
                </div>
              </label>
            </div>

            {/* Assign To - Only show if not common task */}
            {!formData.is_common_task && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Assigned To (Multiple selection allowed)
              </label>
              <div style={{ 
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                maxHeight: '250px',
                overflowY: 'auto'
              }}>
                {getAssignableUsers().filter(u => u.role !== 'educator' && u.full_name).map((u) => {
                  // Clean display name by removing numeric IDs
                  const cleanName = u.full_name.replace(/\s+\d+$/, '').trim();
                  return (
                    <label 
                      key={u.id} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={formData.assigned_to.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, assigned_to: [...formData.assigned_to, u.id] });
                          } else {
                            setFormData({ ...formData, assigned_to: formData.assigned_to.filter(id => id !== u.id) });
                          }
                        }}
                        style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{cleanName}</span>
                    </label>
                  );
                })}
              </div>
              {getAssignableUsers().filter(u => u.role !== 'educator' && u.full_name).length === 0 && (
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#DC2626' }}>
                  ⚠️ No team members found. Please add users under your management first.
                </p>
              )}
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                {user?.role === 'manager' || user?.role === 'team_lead' 
                  ? 'Select from your direct reports.' 
                  : 'Select one or more users to assign this task.'}
              </p>
              {formData.assigned_to.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {formData.assigned_to.map(userId => {
                    const assignedUser = users.find(u => u.id === userId);
                    if (!assignedUser) return null;
                    // Clean display name for chips as well
                    const cleanName = assignedUser.full_name.replace(/\s+\d+$/, '').trim();
                    return (
                      <span key={userId} style={{
                        padding: '4px 10px',
                        background: '#EEF2FF',
                        color: '#4338CA',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {cleanName}
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, assigned_to: formData.assigned_to.filter(id => id !== userId) })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', color: '#4338CA' }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* Work Along With */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Work Along With
              </label>
              <div style={{ 
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                maxHeight: '250px',
                overflowY: 'auto'
              }}>
                {users.filter(u => u.role !== 'educator' && u.full_name).map((u) => {
                  // Clean display name
                  const cleanName = u.full_name.replace(/\s+\d+$/, '').trim();
                  return (
                    <label 
                      key={u.id} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={formData.work_along_with.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, work_along_with: [...formData.work_along_with, u.id] });
                          } else {
                            setFormData({ ...formData, work_along_with: formData.work_along_with.filter(id => id !== u.id) });
                          }
                        }}
                        style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{cleanName}</span>
                    </label>
                  );
                })}
              </div>
              {users.filter(u => u.role !== 'educator' && u.full_name).length === 0 && (
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#DC2626' }}>
                  ⚠️ No team members found. Please add users first.
                </p>
              )}
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                Select users who will be notified of task updates
              </p>
              {formData.work_along_with.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {formData.work_along_with.map(userId => {
                    const workUser = users.find(u => u.id === userId);
                    return workUser ? (
                      <span key={userId} style={{
                        padding: '4px 10px',
                        background: '#EEF2FF',
                        color: '#4338CA',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {workUser.full_name}
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, work_along_with: formData.work_along_with.filter(id => id !== userId) })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', color: '#4338CA' }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            {/* Priority */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <option key={num} value={num}>{num} {num === 1 ? '(Lowest)' : num === 10 ? '(Highest)' : ''}</option>
                ))}
              </select>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                Scale from 1 (lowest priority) to 10 (highest priority)
              </p>
            </div>

            {/* Deadline */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Deadline / ETA (Optional)
              </label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            {/* Research Notes */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Research Notes (Optional)
              </label>
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                Add detailed research notes, findings, or additional context for this task
              </p>
              <RichTextEditor
                value={formData.research_notes}
                onChange={(value) => setFormData({ ...formData, research_notes: value })}
                placeholder="Enter research notes, findings, references..."
                minHeight="150px"
              />
            </div>

            {/* Document Links */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Document Links (Optional)
              </label>
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                Add links to related documents, resources, or references
              </p>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Title (optional)"
                  value={newDocLink.title}
                  onChange={(e) => setNewDocLink({ ...newDocLink, title: e.target.value })}
                  style={{
                    flex: '0 0 150px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="url"
                  placeholder="https://..."
                  value={newDocLink.url}
                  onChange={(e) => setNewDocLink({ ...newDocLink, url: e.target.value })}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                <button
                  type="button"
                  onClick={addDocumentLink}
                  style={{
                    padding: '10px 16px',
                    background: '#7077FE',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Add
                </button>
              </div>
              {formData.document_links.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {formData.document_links.map((doc, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)'
                    }}>
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{
                        color: '#7077FE',
                        textDecoration: 'none',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <LinkIcon size={14} />
                        {doc.title || doc.url}
                      </a>
                      <button
                        type="button"
                        onClick={() => removeDocumentLink(idx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#EF4444',
                          padding: '4px'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* File Attachments */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Attachments (Optional)
              </label>
              <div
                onClick={() => document.getElementById('file-input').click()}
                style={{
                  border: '2px dashed #7077FE',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: '#f9f9ff',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0ff'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f9f9ff'}
              >
                <Upload size={32} style={{ margin: '0 auto 12px', color: '#7077FE' }} />
                <p style={{ margin: '0 0 8px', fontWeight: '600', color: '#333' }}>
                  Click to upload files
                </p>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  Supported: JPG, PNG, GIF, PDF, DOCX (Max 10MB per file)
                </p>
              </div>
              <input
                id="file-input"
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.pdf,.docx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {/* Attachment List */}
              {attachments.length > 0 && (
                <div style={{ marginTop: '16px', display: 'grid', gap: '12px' }}>
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: '#f5f7fa',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {getFileIcon(file.content_type)}
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
                          {file.filename}
                        </p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#ff4444',
                          padding: '4px'
                        }}
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => navigate('/dashboard/tasks')}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="gradient-btn"
                style={{ padding: '12px 32px', fontSize: '16px' }}
              >
                {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Task' : 'Create Task')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskCreate;
